import React from 'react'
import {useDrawer} from '_context'
import {getShopTheImageModuleId} from '_customHooks/page-designer'
import {ShopTheImage, SortByTemplate, LoginDrawer, ResetPasswordDrawer, MinicartHeader, MinicartBody} from '_uiComponents'
import {useIntl} from 'react-intl'

/**
 * Interface representing the data required for the Sort By drawer
 * @interface SortByData
 * @property {string} sortLabel - The label to be displayed in the sort drawer
 * @property {Array<{value: string, label: string}>} optionsWithUrls - Array of sorting options with their values and labels
 * @property {string} [defaultValue] - Optional default selected value
 * @property {function} handleOnChange - Callback function when sort option changes
 */
interface SortByData {
    sortLabel: string
    optionsWithUrls: Array<{value: string; label: string}>
    defaultValue?: string
    handleOnChange: (value: string) => void
}

/**
 * Interface representing the data required for the Login drawer
 * @interface LoginDrawerData
 * @property {boolean} phonelogin - Flag indicating if phone login is enabled
 * @property {string} hostlink - Host URL for login
 */
interface LoginDrawerData {
    phonelogin?: boolean
    hostlink?: string
}

/**
 * Interface representing the data required for the Reset Password drawer
 * @interface ResetPasswordData
 * @property {boolean} phonelogin - Flag indicating if phone login is enabled
 * @property {string} registerUrl - URL for registration
 */
interface ResetPasswordData {
    phonelogin?: boolean
    registerUrl?: string
}

/**
 * Interface representing the data required for the Minicart drawer
 * @interface MinicartData
 * @property {boolean} [hasHiddenPrices] - Optional flag indicating if prices should be hidden
 * @property {any} minicartPreferences - Preferences for minicart display
 * @property {function} onClose - Callback function when minicart is closed
 * @property {number} [autoClosingTimeout] - Optional timeout in milliseconds for auto-closing
 */
interface MinicartData {
    hasHiddenPrices?: boolean
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    minicartPreferences: any
    onClose: () => void
    autoClosingTimeout?: number
}

/**
 * Custom hook that provides functions to open various drawer components
 * @returns {Object} Object containing functions to open different types of drawers
 * @property {function} openShopTheImageDrawer - Opens a drawer displaying shoppable images
 * @property {function} openSortByDrawer - Opens a drawer with sorting options
 * @property {function} openLoginDrawer - Opens a login drawer
 * @property {function} openResetPasswordDrawer - Opens a password reset drawer
 * @property {function} openMinicartDrawer - Opens a minicart drawer
 */
export const useOpenDrawer = () => {
    const {openDrawer} = useDrawer()
    const {formatMessage} = useIntl()

    /**
     * Opens a drawer displaying shoppable images
     * @param {string} moduleId - Module id used to resolve placeholder data
     */
    const openShopTheImageDrawer = (moduleId?: string) => {
        // Drawer opens only if the server-rendered placeholder exists in the DOM.
        // This avoids opening on SSR or when the module is not rendered.
        if (!moduleId || typeof document === 'undefined') return
        const placeholder = document.querySelector(
            `[data-testid="shop-the-image-placeholder"][data-module-id="${moduleId}"]`,
        ) as HTMLElement | null
        if (!placeholder) return
        const moduleTitle = placeholder.getAttribute('data-module-title') || ''
        const products = placeholder.getAttribute('data-products') || ''
        const analyticsList = placeholder.getAttribute('data-analytics-list') || ''
        if (!products) return
        openDrawer({
            title: moduleTitle,
            variant: 'shopTheImage',
            motionProps: {
                variants: {
                    enter: {x: 0, y: 0, transition: {duration: 0}},
                    exit: {x: 0, y: 0, transition: {duration: 0}},
                },
            },
            body: <ShopTheImage productsIds={products} analyticsList={analyticsList} />,
        })
    }

    // Handles shop-the-image CTA clicks in a single place to avoid copy/paste in components.
    // Returns true when it intercepted the click and opened the drawer.
    const handleShopTheImageClick = (event: {preventDefault?: () => void} | undefined, ctaLink?: string) => {
        const moduleId = getShopTheImageModuleId(ctaLink)
        if (!moduleId) return false
        if (event?.preventDefault) event.preventDefault()
        openShopTheImageDrawer(moduleId)
        return true
    }

    /**
     * Opens a drawer with sorting options
     * @param {SortByData} data - Data required for the sort drawer
     */
    const openSortByDrawer = (data: SortByData) => {
        openDrawer({
            title: data.sortLabel,
            variant: 'sortBy',
            body: <SortByTemplate sortingOptions={data.optionsWithUrls} defaultValue={data.defaultValue} handleOnChange={data.handleOnChange} />,
        })
    }

    /**
     * Opens a login drawer
     * @param {LoginDrawerData} data - Data required for the login drawer
     */
    const openLoginDrawer = (data: LoginDrawerData) => {
        openDrawer({
            title: formatMessage({
                id: 'login.offcanvas.title',
                defaultMessage: 'Log in',
            }),
            titleIcon: 'IconUser',
            variant: 'logIn',
            size: 'md',
            closingIcon: 'IconCloseLight',
            closingIconSize: 'lg',
            body: <LoginDrawer phonelogin={data.phonelogin} hostlink={data.hostlink} />,
        })
    }

    /**
     * Opens a password reset drawer
     * @param {ResetPasswordData} data - Data required for the reset password drawer
     */
    const openResetPasswordDrawer = (data: ResetPasswordData) => {
        openDrawer({
            title: formatMessage({
                id: 'label.resetPassword',
                defaultMessage: 'Modify password',
            }),
            titleIcon: 'IconUser',
            variant: 'logIn',
            size: 'md',
            closingIcon: 'IconCloseLight',
            closingIconSize: 'lg',
            body: <ResetPasswordDrawer phonelogin={data.phonelogin} registerUrl={data.registerUrl} />,
        })
    }

    /**
     * Opens a minicart drawer
     * @param {MinicartData} data - Data required for the minicart drawer
     */
    const openMinicartDrawer = (data: MinicartData) => {
        openDrawer({
            titleIcon: 'IconCart',
            title: <MinicartHeader />,
            body: <MinicartBody minicartPreferences={data.minicartPreferences} />,
            onClose: data.onClose,
            autoClosingTimeout: data.autoClosingTimeout,
            variant: 'minicart',
        })
    }

    return {
        openShopTheImageDrawer,
        handleShopTheImageClick,
        openSortByDrawer,
        openLoginDrawer,
        openResetPasswordDrawer,
        openMinicartDrawer,
    }
}
